(() => {
  const SELECTORS = {
    status: "[data-loop-status]",
    scheduled: "[data-loop-scheduled-orders]",
    history: "[data-loop-order-history]",
    subscription: "[data-loop-subscription-details]",
    account: "[data-loop-account-details]",
    currentMini: "[data-loop-current-mini]",
    activeSet: "[data-loop-active-set]",
    upsells: "[data-loop-upsells]",
    modal: "[data-loop-modal]",
    modalContent: "[data-loop-modal-content]",
  };

  const escapeHtml = (value = "") =>
    String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");

  const firstValue = (...values) =>
    values.find((value) => value !== undefined && value !== null && value !== "");

  const getPath = (object, path) =>
    path.split(".").reduce((value, key) => value?.[key], object);

  const unwrap = (payload) => payload?.data ?? payload?.result ?? payload ?? {};

  const arrayFrom = (payload, keys) => {
    const value = unwrap(payload);
    if (Array.isArray(value)) return value;
    for (const key of keys) {
      const candidate = getPath(value, key);
      if (Array.isArray(candidate)) return candidate;
    }
    return [];
  };

  const toEpochSeconds = (value) => {
    if (!value) return null;
    if (typeof value === "number") {
      return value > 100000000000 ? Math.round(value / 1000) : Math.round(value);
    }
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric > 100000000) {
      return numeric > 100000000000 ? Math.round(numeric / 1000) : Math.round(numeric);
    }
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : Math.round(parsed / 1000);
  };

  const toDate = (value) => {
    const epoch = toEpochSeconds(value);
    return epoch ? new Date(epoch * 1000) : null;
  };

  const dateValue = (object) =>
    firstValue(
      object?.billingDateEpoch,
      object?.nextBillingDateEpoch,
      object?.scheduledBillingDateEpoch,
      object?.orderDateEpoch,
      object?.billingDate,
      object?.nextBillingDate,
      object?.scheduledAt,
      object?.scheduledDate,
      object?.orderDate,
      object?.createdAt
    );

  const formatDate = (value, options = {}) => {
    const date = value instanceof Date ? value : toDate(value);
    if (!date) return "Date unavailable";
    return new Intl.DateTimeFormat(undefined, {
      month: options.short ? "short" : "long",
      day: "numeric",
      year: options.year === false ? undefined : "numeric",
    }).format(date);
  };

  const formatMoney = (value, currency = "USD") => {
    if (value === undefined || value === null || value === "") return "";
    if (typeof value === "string" && /[^\d.,-]/.test(value)) return value;
    const amount = Number(value);
    if (!Number.isFinite(amount)) return String(value);
    const normalized = amount > 10000 ? amount / 100 : amount;
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
      }).format(normalized);
    } catch {
      return String(value);
    }
  };

  const subscriptionId = (subscription) =>
    firstValue(subscription?.subscriptionId, subscription?.id, subscription?.loopSubscriptionId);

  const lineId = (line) =>
    firstValue(line?.lineId, line?.id, line?.subscriptionLineId, line?.loopLineId);

  const linesFrom = (subscription) =>
    arrayFrom(subscription, [
      "lines",
      "lineItems",
      "subscriptionLines",
      "items",
      "products",
      "subscription.lines",
    ]);

  const lineTitle = (line) =>
    firstValue(
      line?.productTitle,
      line?.title,
      line?.name,
      line?.product?.title,
      line?.variant?.productTitle,
      "Subscription item"
    );

  const lineVariant = (line) => {
    const value = firstValue(
      line?.variantTitle,
      line?.variant?.title,
      line?.sellingPlanName,
      ""
    );
    return value === "Default Title" ? "" : value;
  };

  const lineImage = (line) =>
    firstValue(
      line?.imageUrl,
      line?.image,
      line?.productImage,
      line?.productImageUrl,
      line?.variantImage,
      line?.variantImageUrl,
      line?.product?.featuredImage?.url,
      line?.product?.image?.url,
      line?.variant?.image?.url,
      ""
    );

  const lineQuantity = (line) => Number(firstValue(line?.quantity, 1));

  const orderLines = (order) =>
    arrayFrom(order, ["lines", "lineItems", "items", "products", "order.lines"]);

  const orderTitle = (order) => {
    const lines = orderLines(order);
    if (lines.length) {
      const names = lines.map(lineTitle).filter(Boolean);
      if (names.length > 2) return `${names.slice(0, 2).join(", ")} +${names.length - 2} more`;
      if (names.length) return names.join(", ");
    }
    return firstValue(order?.name, order?.orderName, order?.title, "Subscription order");
  };

  const statusLabel = (value) => {
    if (!value) return "Active";
    return String(value)
      .replaceAll("_", " ")
      .replace(/\b\w/g, (character) => character.toUpperCase());
  };

  class LoopPortal {
    constructor(root) {
      this.root = root;
      this.apiOrigin = root.dataset.apiOrigin.replace(/\/$/, "");
      this.apiVersion = root.dataset.apiVersion;
      this.authEndpoint = root.dataset.authEndpoint;
      this.customerName = root.dataset.customerName || "Babe";
      this.customerEmail = root.dataset.customerEmail || "";
      this.leadDays = Number(root.dataset.choiceLeadDays || 7);
      this.cancelUrl = root.dataset.cancelUrl || "";
      this.token = "GTnHUDPPT7kyQHxOI69WgLvljC1gV1amgAExmM0mrLmNwn8lJubxNyKBCcvUTS3A";
      this.state = {
        customer: {},
        subscriptions: [],
        activeSubscription: null,
        schedule: [],
        history: [],
        upsells: [],
        frequencies: [],
      };
      this.busy = false;
      this.toastTimer = null;
      this.bindEvents();
      this.setMilestoneCount();
      this.init();
    }

    bindEvents() {
      this.root.addEventListener("click", (event) => {
        const tab = event.target.closest("[data-loop-tab]");
        if (tab) {
          this.selectTab(tab.dataset.loopTab);
          return;
        }

        if (event.target.closest("[data-loop-scroll-products]")) {
          this.root
            .querySelector("[data-loop-product-section]")
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
          return;
        }

        const productButton = event.target.closest("[data-loop-product-button]");
        if (productButton) {
          const card = productButton.closest("[data-loop-product-card]");
          this.openProductAction(card);
          return;
        }

        if (event.target.closest("[data-loop-modal-close]")) {
          this.closeModal();
          return;
        }

        const actionButton = event.target.closest("[data-loop-action]");
        if (actionButton) this.handleAction(actionButton);
      });

      this.root.addEventListener("change", (event) => {
        if (event.target.matches("[data-loop-subscription-select]")) {
          this.changeSubscription(event.target.value);
        }
      });

      this.root.addEventListener("submit", (event) => {
        const form = event.target.closest("[data-loop-form]");
        if (!form) return;
        event.preventDefault();
        this.handleForm(form);
      });
    }

    async init() {
      this.setStatus("Loading your membership…");
      try {
        await this.authenticate();
        const [subscriptionsResponse, customerResponse] = await Promise.all([
          this.api("/subscription"),
          this.api("/customer/"),
        ]);

        this.state.subscriptions = arrayFrom(subscriptionsResponse, [
          "subscriptions",
          "items",
          "content",
          "records",
        ]);
        this.state.customer = unwrap(customerResponse);
        this.state.activeSubscription = this.pickActiveSubscription();

        if (!this.state.activeSubscription) {
          this.renderNoSubscription();
          this.setStatus(
            "Your account is connected, but no Loop subscription was found.",
            "error"
          );
          return;
        }

        await this.loadSubscriptionData();
        this.renderAll();
        this.setStatus("Your membership is ready.", "success");
        window.setTimeout(() => this.hideStatus(), 2600);
      } catch (error) {
        this.renderError(error);
      }
    }

    async authenticate() {
      const response = await fetch(this.authEndpoint, {
        method: "POST",
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      });
      const payload = await this.readResponse(response);
      const token = firstValue(
        payload?.data?.accessToken,
        payload?.accessToken,
        payload?.data?.token
      );
      if (!response.ok || !token) {
        const error = new Error(
          response.status === 401
            ? "Please log in with the Shopify account connected to your Loop subscription."
            : firstValue(payload?.message, "Loop authentication could not be completed.")
        );
        error.status = response.status;
        throw error;
      }
      this.token = token;
    }

    async api(path, options = {}) {
      const response = await fetch(
        `${this.apiOrigin}/storefront/${this.apiVersion}${path}`,
        {
          method: options.method || "GET",
          credentials: "omit",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${this.token}`,
            ...(options.body ? { "Content-Type": "application/json" } : {}),
          },
          body: options.body ? JSON.stringify(options.body) : undefined,
        }
      );
      const payload = await this.readResponse(response);
      if (!response.ok) {
        const error = new Error(
          firstValue(
            payload?.message,
            payload?.error?.message,
            payload?.errors?.[0]?.message,
            `Loop returned ${response.status}.`
          )
        );
        error.status = response.status;
        error.payload = payload;
        throw error;
      }
      return payload;
    }

    async readResponse(response) {
      const text = await response.text();
      if (!text) return {};
      try {
        return JSON.parse(text);
      } catch {
        return { message: text };
      }
    }

    pickActiveSubscription() {
      if (!this.state.subscriptions.length) return null;
      return (
        this.state.subscriptions.find((subscription) =>
          ["active", "paused"].includes(String(subscription?.status || "").toLowerCase())
        ) || this.state.subscriptions[0]
      );
    }

    async loadSubscriptionData() {
      const id = subscriptionId(this.state.activeSubscription);
      if (!id) throw new Error("Loop returned a subscription without an ID.");

      const results = await Promise.allSettled([
        this.api(`/subscription/${id}`),
        this.api(`/subscription/${id}/order/schedule`),
        this.api(`/subscription/${id}/order/history`),
        this.api(`/subscription/${id}/upsell/product?pageNo=1&pageSize=8`),
        this.api(`/subscription/${id}/frequency`),
      ]);

      if (results[0].status === "fulfilled") {
        const detail = unwrap(results[0].value);
        this.state.activeSubscription =
          detail?.subscription ?? detail ?? this.state.activeSubscription;
        const index = this.state.subscriptions.findIndex(
          (item) => String(subscriptionId(item)) === String(id)
        );
        if (index >= 0) this.state.subscriptions[index] = this.state.activeSubscription;
      }

      this.state.schedule =
        results[1].status === "fulfilled"
          ? arrayFrom(results[1].value, ["orders", "schedule", "items", "content", "records"])
          : [];
      this.state.history =
        results[2].status === "fulfilled"
          ? arrayFrom(results[2].value, ["orders", "history", "items", "content", "records"])
          : [];
      this.state.upsells =
        results[3].status === "fulfilled"
          ? arrayFrom(results[3].value, ["products", "items", "content", "records"])
          : [];
      this.state.frequencies =
        results[4].status === "fulfilled"
          ? arrayFrom(results[4].value, ["frequencies", "items", "content", "records"])
          : [];
    }

    async changeSubscription(id) {
      const next = this.state.subscriptions.find(
        (subscription) => String(subscriptionId(subscription)) === String(id)
      );
      if (!next || this.busy) return;
      this.state.activeSubscription = next;
      this.setStatus("Switching subscription…");
      try {
        await this.loadSubscriptionData();
        this.renderAll();
        this.setStatus("Subscription updated.", "success");
        window.setTimeout(() => this.hideStatus(), 1800);
      } catch (error) {
        this.setStatus(error.message, "error");
      }
    }

    renderAll() {
      this.renderHero();
      this.renderScheduled();
      this.renderHistory();
      this.renderSubscription();
      this.renderAccount();
      this.renderMilestones();
      this.renderUpsells();
    }

    renderHero() {
      const subscription = this.state.activeSubscription;
      const line = linesFrom(subscription)[0] || {};
      const title = lineTitle(line);
      const variant = lineVariant(line);
      const image = lineImage(line);
      const nextOrder = this.state.schedule[0] || {};
      const billingDate =
        toDate(dateValue(nextOrder)) || toDate(dateValue(subscription));

      const mini = this.root.querySelector(SELECTORS.currentMini);
      if (mini) {
        mini.innerHTML = `
          ${
            image
              ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(title)}" loading="lazy">`
              : '<div class="loop-product-placeholder" aria-hidden="true">✿</div>'
          }
          <p>Currently selected:<br><strong>${escapeHtml(title)}</strong>${
            variant ? `<br>${escapeHtml(variant)}` : ""
          }</p>
        `;
      }

      const activeSet = this.root.querySelector(SELECTORS.activeSet);
      if (activeSet) {
        activeSet.innerHTML = `
          ${
            image
              ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(title)}" loading="lazy">`
              : '<div class="loop-product-placeholder" aria-hidden="true">✿</div>'
          }
          <div>
            <p class="loop-kicker">Your next set</p>
            <h2>${escapeHtml(title)}</h2>
            <p>${variant ? `${escapeHtml(variant)} · ` : ""}Quantity ${lineQuantity(
              line
            )}. Choose any set below to make a swap.</p>
          </div>
        `;
      }

      const note = this.root.querySelector("[data-loop-choice-note]");
      const deadline = this.root.querySelector("[data-loop-deadline]");
      if (billingDate && note && deadline) {
        const cutoff = new Date(billingDate);
        cutoff.setDate(cutoff.getDate() - this.leadDays);
        const daysLeft = Math.ceil((cutoff.getTime() - Date.now()) / 86400000);
        const progress = Math.max(0, Math.min(100, ((this.leadDays - daysLeft) / this.leadDays) * 100));
        const remaining = Math.max(0, daysLeft);
        deadline.hidden = false;
        deadline.querySelector("[data-loop-days-left]").textContent =
          remaining === 0 ? "Selection closes today" : `${remaining} day${remaining === 1 ? "" : "s"} left to choose`;
        deadline.querySelector("[data-loop-deadline-date]").textContent =
          `Closes ${formatDate(cutoff, { short: true })}`;
        const progressBar = deadline.querySelector("[role='progressbar']");
        progressBar.setAttribute("aria-valuenow", String(Math.round(progress)));
        deadline.querySelector("[data-loop-progress]").style.width = `${progress}%`;
        note.textContent = `Your next order is scheduled for ${formatDate(
          billingDate
        )}. If you do not choose a new set, your current selection stays in place.`;
      }
    }

    renderScheduled() {
      const container = this.root.querySelector(SELECTORS.scheduled);
      if (!container) return;
      if (!this.state.schedule.length) {
        container.innerHTML = this.emptyState(
          "No scheduled orders yet",
          "Your next Loop order will appear here as soon as it is scheduled."
        );
        return;
      }

      container.innerHTML = this.state.schedule
        .map((order, index) => {
          const date = toDate(dateValue(order));
          const total = firstValue(
            order?.totalPrice,
            order?.total,
            order?.amount,
            order?.orderTotal
          );
          const currency = firstValue(order?.currencyCode, order?.currency, "USD");
          return `
            <article class="loop-order-card">
              <div class="loop-order-card__date">
                <strong>${date ? date.getDate() : "—"}</strong>
                <span>${date ? new Intl.DateTimeFormat(undefined, { month: "short" }).format(date) : "Pending"}</span>
              </div>
              <div class="loop-order-card__content">
                <h3>${escapeHtml(orderTitle(order))}</h3>
                <p>${escapeHtml(formatDate(dateValue(order)))}${
                  total !== undefined ? ` · ${escapeHtml(formatMoney(total, currency))}` : ""
                }</p>
              </div>
              <div class="loop-order-card__actions">
                ${
                  index === 0
                    ? `
                      <button class="loop-button loop-button--small loop-button--ghost" type="button" data-loop-action="reschedule">Reschedule</button>
                      <button class="loop-button loop-button--small loop-button--ghost" type="button" data-loop-action="skip-order">Skip</button>
                      <button class="loop-button loop-button--small loop-button--pink" type="button" data-loop-action="order-now">Order now</button>
                    `
                    : ""
                }
              </div>
            </article>
          `;
        })
        .join("");
    }

    renderHistory() {
      const container = this.root.querySelector(SELECTORS.history);
      if (!container) return;
      if (!this.state.history.length) {
        container.innerHTML = this.emptyState(
          "No past orders yet",
          "Completed subscription orders will be saved here."
        );
        return;
      }

      container.innerHTML = this.state.history
        .map((order) => {
          const date = toDate(dateValue(order));
          const state = firstValue(order?.status, order?.orderStatus, "Delivered");
          return `
            <article class="loop-order-card">
              <div class="loop-order-card__date">
                <strong>${date ? date.getDate() : "—"}</strong>
                <span>${date ? new Intl.DateTimeFormat(undefined, { month: "short" }).format(date) : "Order"}</span>
              </div>
              <div class="loop-order-card__content">
                <h3>${escapeHtml(orderTitle(order))}</h3>
                <p>${escapeHtml(formatDate(dateValue(order)))} · ${escapeHtml(
                  statusLabel(state)
                )}</p>
              </div>
              <div class="loop-order-card__actions">
                <span class="loop-button loop-button--small loop-button--ghost" aria-label="Order status">${escapeHtml(
                  statusLabel(state)
                )}</span>
              </div>
            </article>
          `;
        })
        .join("");
    }

    renderSubscription() {
      const container = this.root.querySelector(SELECTORS.subscription);
      if (!container) return;
      const subscription = this.state.activeSubscription;
      const id = subscriptionId(subscription);
      const status = String(firstValue(subscription?.status, "active")).toLowerCase();
      const nextBilling = firstValue(
        subscription?.nextBillingDateEpoch,
        subscription?.nextBillingDate,
        dateValue(this.state.schedule[0])
      );
      const cadence = firstValue(
        subscription?.billingPolicy?.displayName,
        subscription?.frequency,
        subscription?.deliveryPolicy?.displayName,
        subscription?.sellingPlanName,
        "See Loop plan"
      );

      const selector =
        this.state.subscriptions.length > 1
          ? `
            <label class="loop-detail-card__label" for="LoopSubscriptionPicker-${escapeHtml(
              id
            )}">Viewing subscription</label>
            <select id="LoopSubscriptionPicker-${escapeHtml(
              id
            )}" data-loop-subscription-select>
              ${this.state.subscriptions
                .map((item, index) => {
                  const itemId = subscriptionId(item);
                  const itemLine = linesFrom(item)[0];
                  const label = itemLine ? lineTitle(itemLine) : `Subscription ${index + 1}`;
                  return `<option value="${escapeHtml(itemId)}"${
                    String(itemId) === String(id) ? " selected" : ""
                  }>${escapeHtml(label)}</option>`;
                })
                .join("")}
            </select>
          `
          : `<span class="loop-detail-card__label">Subscription ID</span><h3>#${escapeHtml(
              id
            )}</h3>`;

      container.innerHTML = `
        <article class="loop-detail-card">${selector}</article>
        <article class="loop-detail-card">
          <span class="loop-detail-card__label">Status</span>
          <h3>${escapeHtml(statusLabel(status))}</h3>
          <div class="loop-detail-card__actions">
            <button class="loop-button loop-button--small loop-button--ghost" type="button" data-loop-action="${
              status === "paused" ? "resume" : "pause"
            }">${status === "paused" ? "Resume subscription" : "Pause subscription"}</button>
          </div>
        </article>
        <article class="loop-detail-card">
          <span class="loop-detail-card__label">Next billing date</span>
          <h3>${escapeHtml(formatDate(nextBilling))}</h3>
          <div class="loop-detail-card__actions">
            <button class="loop-button loop-button--small loop-button--ghost" type="button" data-loop-action="reschedule">Change date</button>
          </div>
        </article>
        <article class="loop-detail-card">
          <span class="loop-detail-card__label">Delivery cadence</span>
          <h3>${escapeHtml(String(cadence))}</h3>
          <div class="loop-detail-card__actions">
            <button class="loop-button loop-button--small loop-button--ghost" type="button" data-loop-action="frequency"${
              this.state.frequencies.length ? "" : " disabled"
            }>Change frequency</button>
          </div>
        </article>
        <article class="loop-detail-card loop-detail-card--wide">
          <span class="loop-detail-card__label">Included products</span>
          ${linesFrom(subscription)
            .map(
              (line) =>
                `<p><strong>${escapeHtml(lineTitle(line))}</strong>${
                  lineVariant(line) ? ` · ${escapeHtml(lineVariant(line))}` : ""
                } · Qty ${lineQuantity(line)}</p>`
            )
            .join("")}
        </article>
      `;
    }

    renderAccount() {
      const container = this.root.querySelector(SELECTORS.account);
      if (!container) return;
      const customer = this.state.customer?.customer ?? this.state.customer;
      const subscription = this.state.activeSubscription;
      const address =
        subscription?.shippingAddress ??
        subscription?.deliveryAddress ??
        customer?.defaultAddress ??
        customer?.shippingAddress ??
        arrayFrom(customer, ["addresses"])[0] ??
        {};

      container.innerHTML = `
        <article class="loop-detail-card">
          <span class="loop-detail-card__label">Customer</span>
          <h3>${escapeHtml(
            firstValue(
              customer?.name,
              [customer?.firstName, customer?.lastName].filter(Boolean).join(" "),
              this.customerName
            )
          )}</h3>
          ${
            firstValue(customer?.email, this.customerEmail)
              ? `<p>${escapeHtml(firstValue(customer?.email, this.customerEmail))}</p>`
              : ""
          }
        </article>
        <article class="loop-detail-card">
          <span class="loop-detail-card__label">Shipping address</span>
          <h3>${escapeHtml(
            [address?.firstName, address?.lastName].filter(Boolean).join(" ") ||
              this.customerName
          )}</h3>
          <p>${escapeHtml(firstValue(address?.address1, "No address on file"))}</p>
          ${
            address?.address2 ? `<p>${escapeHtml(address.address2)}</p>` : ""
          }
          <p>${escapeHtml(
            [address?.city, address?.provinceCode ?? address?.province, address?.zip]
              .filter(Boolean)
              .join(", ")
          )}</p>
          <p>${escapeHtml(
            firstValue(address?.countryCode, address?.country, "")
          )}</p>
          <div class="loop-detail-card__actions">
            <button class="loop-button loop-button--small loop-button--ghost" type="button" data-loop-action="address">Update shipping address</button>
          </div>
        </article>
      `;
    }

    renderMilestones() {
      const milestones = [...this.root.querySelectorAll("[data-loop-milestone]")];
      if (!milestones.length) return;
      const deliveredCount = this.state.history.filter((order) => {
        const status = String(firstValue(order?.status, order?.orderStatus, "")).toLowerCase();
        return !["cancelled", "canceled", "failed", "voided"].includes(status);
      }).length;
      let unlocked = 0;
      milestones.forEach((milestone) => {
        const isUnlocked = deliveredCount >= Number(milestone.dataset.threshold);
        milestone.classList.toggle("is-unlocked", isUnlocked);
        if (isUnlocked) unlocked += 1;
      });
      const progress = milestones.length ? (unlocked / milestones.length) * 100 : 0;
      const bar = this.root.querySelector("[data-loop-milestone-progress]");
      if (bar) bar.style.width = `${progress}%`;
    }

    normalizeUpsell(item) {
      const product = item?.product ?? item;
      const variants = arrayFrom(product, ["variants", "productVariants"]);
      const variant = item?.variant ?? product?.variant ?? variants[0] ?? {};
      const image = firstValue(
        item?.imageUrl,
        product?.imageUrl,
        product?.featuredImage?.url,
        product?.image?.url,
        variant?.imageUrl,
        variant?.image?.url,
        Array.isArray(product?.images) ? product.images[0]?.url ?? product.images[0] : ""
      );
      return {
        title: firstValue(product?.title, item?.productTitle, item?.title, "Recommended add-on"),
        variantTitle: firstValue(variant?.title, item?.variantTitle, ""),
        variantId: firstValue(
          variant?.shopifyId,
          variant?.variantShopifyId,
          variant?.id,
          item?.variantShopifyId
        ),
        image,
        price: firstValue(
          variant?.price,
          variant?.priceAmount,
          item?.price,
          product?.price
        ),
        currency: firstValue(variant?.currencyCode, item?.currencyCode, "USD"),
        profileId: firstValue(
          item?.upsellProfileId,
          product?.upsellProfileId,
          item?.profileId
        ),
      };
    }

    renderUpsells() {
      const container = this.root.querySelector(SELECTORS.upsells);
      if (!container) return;
      const products = this.state.upsells.map((item) => this.normalizeUpsell(item));
      if (!products.length) {
        container.innerHTML = this.emptyState(
          "No add-ons available right now",
          "Check back before your next delivery for fresh member picks.",
          true
        );
        return;
      }

      container.innerHTML = products
        .filter((product) => product.variantId)
        .map(
          (product) => `
            <article
              class="loop-product-card"
              data-loop-product-card
              data-loop-product-action="add-on"
              data-variant-id="${escapeHtml(product.variantId)}"
              data-product-title="${escapeHtml(product.title)}"
              data-variant-title="${escapeHtml(product.variantTitle)}"
              data-product-image="${escapeHtml(product.image)}"
              data-product-price="${escapeHtml(formatMoney(product.price, product.currency))}"
              data-upsell-profile-id="${escapeHtml(product.profileId ?? "")}"
            >
              <div class="loop-product-card__media">
                <span class="loop-product-card__badge">For you</span>
                ${
                  product.image
                    ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(
                        product.title
                      )}" loading="lazy">`
                    : '<div class="loop-product-placeholder" aria-hidden="true">✿</div>'
                }
              </div>
              <div class="loop-product-card__body">
                <div>
                  <h3>${escapeHtml(product.title)}</h3>
                  ${
                    product.variantTitle && product.variantTitle !== "Default Title"
                      ? `<p>${escapeHtml(product.variantTitle)}</p>`
                      : ""
                  }
                </div>
                <strong>${escapeHtml(formatMoney(product.price, product.currency))}</strong>
              </div>
              <button class="loop-product-card__button" type="button" data-loop-product-button>
                Add to next box <span aria-hidden="true">→</span>
              </button>
            </article>
          `
        )
        .join("");
    }

    renderNoSubscription() {
      const message = this.emptyState(
        "No active subscription found",
        "If you subscribed recently, refresh this page in a moment. Otherwise, contact the store team for help."
      );
      [
        SELECTORS.scheduled,
        SELECTORS.history,
        SELECTORS.subscription,
        SELECTORS.account,
        SELECTORS.upsells,
      ].forEach((selector) => {
        const container = this.root.querySelector(selector);
        if (container) container.innerHTML = message;
      });
      this.root.querySelectorAll("[data-loop-action], [data-loop-product-button]").forEach((button) => {
        button.disabled = true;
      });
    }

    renderError(error) {
      this.setStatus(error.message || "The portal could not be loaded.", "error");
      const container = this.root.querySelector(SELECTORS.scheduled);
      if (container) {
        container.innerHTML = this.emptyState(
          "We could not load your membership",
          `${error.message || "Please refresh and try again."} If the problem continues, contact the store team.`
        );
      }
      this.root.querySelectorAll("[data-loop-action], [data-loop-product-button]").forEach((button) => {
        button.disabled = true;
      });
    }

    emptyState(title, message, compact = false) {
      return `
        <div class="loop-empty${compact ? " loop-empty--compact" : ""}">
          <div>
            <span class="loop-empty__icon" aria-hidden="true">✿</span>
            <h3>${escapeHtml(title)}</h3>
            <p>${escapeHtml(message)}</p>
          </div>
        </div>
      `;
    }

    selectTab(name) {
      this.root.querySelectorAll("[data-loop-tab]").forEach((tab) => {
        const active = tab.dataset.loopTab === name;
        tab.classList.toggle("is-active", active);
        tab.setAttribute("aria-selected", String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      this.root.querySelectorAll("[data-loop-panel]").forEach((panel) => {
        panel.hidden = panel.dataset.loopPanel !== name;
      });
    }

    setMilestoneCount() {
      const container = this.root.querySelector("[data-loop-milestones]");
      if (!container) return;
      const count = container.querySelectorAll("[data-loop-milestone]").length;
      container.style.setProperty("--loop-milestone-count", Math.max(1, count));
    }

    setStatus(message, type = "") {
      const status = this.root.querySelector(SELECTORS.status);
      if (!status) return;
      status.hidden = false;
      status.classList.toggle("is-success", type === "success");
      status.classList.toggle("is-error", type === "error");
      const text = status.querySelector("span:last-child");
      if (text) text.textContent = message;
    }

    hideStatus() {
      const status = this.root.querySelector(SELECTORS.status);
      if (status) status.hidden = true;
    }

    setBusy(value, button) {
      this.busy = value;
      this.root
        .querySelectorAll("[data-loop-action], [data-loop-product-button], [data-loop-form] button")
        .forEach((element) => {
          if (value) {
            element.dataset.wasDisabled = String(element.disabled);
            element.disabled = true;
          } else {
            element.disabled = element.dataset.wasDisabled === "true";
            delete element.dataset.wasDisabled;
          }
        });
      if (button) {
        if (value) {
          button.dataset.originalText = button.textContent;
          button.textContent = "Working…";
        } else if (button.dataset.originalText) {
          button.textContent = button.dataset.originalText;
          delete button.dataset.originalText;
        }
      }
    }

    toast(message, isError = false) {
      this.root.querySelector(".loop-toast")?.remove();
      window.clearTimeout(this.toastTimer);
      const toast = document.createElement("div");
      toast.className = `loop-toast${isError ? " is-error" : ""}`;
      toast.setAttribute("role", isError ? "alert" : "status");
      toast.textContent = message;
      this.root.append(toast);
      this.toastTimer = window.setTimeout(() => toast.remove(), 4200);
    }

    async mutate(path, options, successMessage, button) {
      if (this.busy) return;
      this.setBusy(true, button);
      try {
        await this.api(path, options);
        await this.refreshActiveSubscription();
        this.closeModal();
        this.toast(successMessage);
      } catch (error) {
        this.toast(error.message || "That change could not be saved.", true);
      } finally {
        this.setBusy(false, button);
      }
    }

    async refreshActiveSubscription() {
      const id = subscriptionId(this.state.activeSubscription);
      const detail = await this.api(`/subscription/${id}`);
      this.state.activeSubscription = unwrap(detail)?.subscription ?? unwrap(detail);
      await this.loadSubscriptionData();
      this.renderAll();
    }

    handleAction(button) {
      if (this.busy || !this.state.activeSubscription) return;
      const action = button.dataset.loopAction;
      const id = subscriptionId(this.state.activeSubscription);
      if (!id && action !== "cancel") return;

      if (action === "pause") {
        this.confirmAction(
          "Pause your subscription?",
          "Future billing and deliveries will stop until you resume.",
          "Pause subscription",
          () => this.mutate(`/subscription/${id}/pause`, { method: "POST", body: {} }, "Your subscription is paused.", button)
        );
      } else if (action === "resume") {
        this.confirmAction(
          "Resume your subscription?",
          "Loop will reactivate billing and deliveries based on your subscription schedule.",
          "Resume subscription",
          () => this.mutate(`/subscription/${id}/resume`, { method: "POST" }, "Your subscription is active again.", button)
        );
      } else if (action === "skip-order") {
        this.confirmAction(
          "Skip your next order?",
          "Only the next scheduled order will be skipped. Your subscription stays active.",
          "Skip next order",
          () => this.mutate(`/subscription/${id}/skipNext`, { method: "POST", body: {} }, "Your next order was skipped.", button)
        );
      } else if (action === "order-now") {
        this.confirmAction(
          "Place your next order now?",
          "Loop will bill the payment method on file and create the next subscription order.",
          "Order now",
          () => this.mutate(`/subscription/${id}/placeOrder`, { method: "POST", body: {} }, "Your order was placed.", button)
        );
      } else if (action === "reschedule") {
        this.openReschedule();
      } else if (action === "address") {
        this.openAddress();
      } else if (action === "frequency") {
        this.openFrequency();
      } else if (action === "cancel") {
        if (this.cancelUrl) {
          window.location.assign(this.cancelUrl);
        } else {
          this.toast(
            "Add your Loop cancellation-flow URL in this section’s theme settings.",
            true
          );
        }
      }
    }

    openProductAction(card) {
      if (!card || this.busy || !this.state.activeSubscription) return;
      const product = {
        action: card.dataset.loopProductAction,
        variantId: card.dataset.variantId,
        title: card.dataset.productTitle,
        variantTitle: card.dataset.variantTitle,
        image: card.dataset.productImage,
        price: card.dataset.productPrice,
        profileId: card.dataset.upsellProfileId,
      };
      if (!product.variantId) {
        this.toast("This product needs an available Shopify variant.", true);
        return;
      }

      if (product.action === "swap") {
        this.openSwap(product);
      } else {
        this.openAddOn(product);
      }
    }

    productPreview(product) {
      return `
        <div class="loop-swap-preview">
          ${
            product.image
              ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(
                  product.title
                )}">`
              : '<div class="loop-product-placeholder" aria-hidden="true">✿</div>'
          }
          <div>
            <h3>${escapeHtml(product.title)}</h3>
            ${
              product.variantTitle && product.variantTitle !== "Default Title"
                ? `<p>${escapeHtml(product.variantTitle)}</p>`
                : ""
            }
            ${product.price ? `<p><strong>${escapeHtml(product.price)}</strong></p>` : ""}
          </div>
        </div>
      `;
    }

    openSwap(product) {
      const lines = linesFrom(this.state.activeSubscription);
      if (!lines.length) {
        this.toast("Loop did not return a swappable subscription line.", true);
        return;
      }
      this.openModal(`
        <div class="loop-modal__intro">
          <p class="loop-kicker">Update your set</p>
          <h2>Confirm your swap</h2>
          <p>Your selected subscription line will be replaced for future orders.</p>
        </div>
        ${this.productPreview(product)}
        <form class="loop-form" data-loop-form="swap">
          <input type="hidden" name="variantId" value="${escapeHtml(product.variantId)}">
          <div class="loop-form__field loop-form__field--wide">
            <label for="LoopSwapLine">Replace this item</label>
            <select id="LoopSwapLine" name="lineId" required>
              ${lines
                .map(
                  (line) =>
                    `<option value="${escapeHtml(lineId(line))}">${escapeHtml(
                      lineTitle(line)
                    )}${lineVariant(line) ? ` · ${escapeHtml(lineVariant(line))}` : ""}</option>`
                )
                .join("")}
            </select>
          </div>
          <div class="loop-form__field">
            <label for="LoopSwapQuantity">Quantity</label>
            <input id="LoopSwapQuantity" name="quantity" type="number" min="1" max="20" value="1" required>
          </div>
          <div class="loop-form__actions">
            <button class="loop-button loop-button--ghost" type="button" data-loop-modal-close>Keep current set</button>
            <button class="loop-button loop-button--pink" type="submit">Confirm swap</button>
          </div>
        </form>
      `);
    }

    openAddOn(product) {
      this.openModal(`
        <div class="loop-modal__intro">
          <p class="loop-kicker">One-time add-on</p>
          <h2>Add to your next box?</h2>
          <p>This item is added once and will not repeat in later subscription orders.</p>
        </div>
        ${this.productPreview(product)}
        <form class="loop-form" data-loop-form="add-on">
          <input type="hidden" name="variantId" value="${escapeHtml(product.variantId)}">
          <input type="hidden" name="profileId" value="${escapeHtml(product.profileId ?? "")}">
          <div class="loop-form__field">
            <label for="LoopAddQuantity">Quantity</label>
            <input id="LoopAddQuantity" name="quantity" type="number" min="1" max="20" value="1" required>
          </div>
          <div class="loop-form__actions">
            <button class="loop-button loop-button--ghost" type="button" data-loop-modal-close>Not now</button>
            <button class="loop-button loop-button--pink" type="submit">Add to next box</button>
          </div>
        </form>
      `);
    }

    openReschedule() {
      const current = toDate(
        firstValue(
          dateValue(this.state.schedule[0]),
          this.state.activeSubscription?.nextBillingDateEpoch,
          this.state.activeSubscription?.nextBillingDate
        )
      );
      const minimum = new Date();
      minimum.setDate(minimum.getDate() + 1);
      const toInput = (date) => date?.toISOString().slice(0, 10) ?? "";
      this.openModal(`
        <div class="loop-modal__intro">
          <p class="loop-kicker">Delivery date</p>
          <h2>Reschedule your next order</h2>
          <p>Choose a new date for the next subscription billing.</p>
        </div>
        <form class="loop-form" data-loop-form="reschedule">
          <div class="loop-form__field loop-form__field--wide">
            <label for="LoopRescheduleDate">New billing date</label>
            <input id="LoopRescheduleDate" name="date" type="date" min="${toInput(
              minimum
            )}" value="${toInput(current)}" required>
          </div>
          <div class="loop-form__actions">
            <button class="loop-button loop-button--ghost" type="button" data-loop-modal-close>Cancel</button>
            <button class="loop-button loop-button--pink" type="submit">Save new date</button>
          </div>
        </form>
      `);
    }

    currentAddress() {
      const customer = this.state.customer?.customer ?? this.state.customer;
      return (
        this.state.activeSubscription?.shippingAddress ??
        this.state.activeSubscription?.deliveryAddress ??
        customer?.defaultAddress ??
        customer?.shippingAddress ??
        arrayFrom(customer, ["addresses"])[0] ??
        {}
      );
    }

    openAddress() {
      const address = this.currentAddress();
      const field = (name, label, value = "", options = {}) => `
        <div class="loop-form__field${options.wide ? " loop-form__field--wide" : ""}">
          <label for="LoopAddress${name}">${label}</label>
          <input
            id="LoopAddress${name}"
            name="${name}"
            type="${options.type || "text"}"
            value="${escapeHtml(value)}"
            ${options.required ? "required" : ""}
            ${options.autocomplete ? `autocomplete="${options.autocomplete}"` : ""}
          >
        </div>
      `;
      this.openModal(`
        <div class="loop-modal__intro">
          <p class="loop-kicker">Shipping address</p>
          <h2>Where should we send it?</h2>
          <p>This updates the delivery address for your selected Loop subscription.</p>
        </div>
        <form class="loop-form" data-loop-form="address">
          ${field("firstName", "First name", address?.firstName, {
            autocomplete: "given-name",
          })}
          ${field("lastName", "Last name", address?.lastName, {
            required: true,
            autocomplete: "family-name",
          })}
          ${field("company", "Company", address?.company, {
            autocomplete: "organization",
          })}
          ${field("phone", "Phone", address?.phone, {
            type: "tel",
            autocomplete: "tel",
          })}
          ${field("address1", "Address", address?.address1, {
            required: true,
            autocomplete: "address-line1",
            wide: true,
          })}
          ${field("address2", "Apartment, suite, etc.", address?.address2, {
            autocomplete: "address-line2",
            wide: true,
          })}
          ${field("city", "City", address?.city, {
            required: true,
            autocomplete: "address-level2",
          })}
          ${field(
            "provinceCode",
            "Province / state code",
            firstValue(address?.provinceCode, address?.province),
            { autocomplete: "address-level1" }
          )}
          ${field("zip", "Postal code", address?.zip, {
            autocomplete: "postal-code",
          })}
          ${field(
            "countryCode",
            "Country code",
            firstValue(address?.countryCode, address?.country),
            { required: true, autocomplete: "country" }
          )}
          <div class="loop-form__actions">
            <button class="loop-button loop-button--ghost" type="button" data-loop-modal-close>Cancel</button>
            <button class="loop-button loop-button--pink" type="submit">Save address</button>
          </div>
        </form>
      `);
    }

    openFrequency() {
      if (!this.state.frequencies.length) {
        this.toast("No alternate frequencies are available for this subscription.", true);
        return;
      }
      this.openModal(`
        <div class="loop-modal__intro">
          <p class="loop-kicker">Delivery cadence</p>
          <h2>Change your frequency</h2>
          <p>The updated cadence will apply to future subscription orders.</p>
        </div>
        <form class="loop-form" data-loop-form="frequency">
          <div class="loop-form__field loop-form__field--wide">
            <label for="LoopFrequency">New frequency</label>
            <select id="LoopFrequency" name="frequencyIndex" required>
              ${this.state.frequencies
                .map((frequency, index) => {
                  const policy = frequency?.billingPolicy ?? frequency;
                  const label = firstValue(
                    frequency?.displayName,
                    frequency?.name,
                    policy?.displayName,
                    policy?.interval && policy?.intervalCount
                      ? `Every ${policy.intervalCount} ${String(policy.interval).toLowerCase()}`
                      : `Option ${index + 1}`
                  );
                  return `<option value="${index}">${escapeHtml(label)}</option>`;
                })
                .join("")}
            </select>
          </div>
          <div class="loop-form__actions">
            <button class="loop-button loop-button--ghost" type="button" data-loop-modal-close>Cancel</button>
            <button class="loop-button loop-button--pink" type="submit">Update frequency</button>
          </div>
        </form>
      `);
    }

    confirmAction(title, message, buttonLabel, callback) {
      this.openModal(`
        <div class="loop-modal__intro">
          <p class="loop-kicker">Please confirm</p>
          <h2>${escapeHtml(title)}</h2>
          <p>${escapeHtml(message)}</p>
        </div>
        <div class="loop-form__actions" style="margin-top: 26px">
          <button class="loop-button loop-button--ghost" type="button" data-loop-modal-close>Go back</button>
          <button class="loop-button loop-button--pink" type="button" data-loop-confirm>${escapeHtml(
            buttonLabel
          )}</button>
        </div>
      `);
      const confirm = this.root.querySelector("[data-loop-confirm]");
      confirm?.addEventListener("click", callback, { once: true });
    }

    async handleForm(form) {
      if (this.busy) return;
      const id = subscriptionId(this.state.activeSubscription);
      const data = Object.fromEntries(new FormData(form));
      const submit = form.querySelector("[type='submit']");
      const action = form.dataset.loopForm;

      if (action === "swap") {
        await this.mutate(
          `/subscription/${id}/line/${encodeURIComponent(data.lineId)}/swap`,
          {
            method: "PUT",
            body: {
              variantShopifyId: Number(data.variantId),
              quantity: Number(data.quantity),
            },
          },
          "Your next set was updated.",
          submit
        );
      } else if (action === "add-on") {
        const body = {
          variantShopifyId: Number(data.variantId),
          quantity: Number(data.quantity),
          isUpsell: true,
        };
        if (data.profileId) body.upsellProfileId = Number(data.profileId);
        await this.mutate(
          `/subscription/${id}/line/once`,
          { method: "POST", body },
          "Added to your next box.",
          submit
        );
      } else if (action === "reschedule") {
        const date = new Date(`${data.date}T12:00:00`);
        await this.mutate(
          `/subscription/${id}/reschedule`,
          {
            method: "POST",
            body: {
              newBillingDateEpoch: Math.round(date.getTime() / 1000),
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
          },
          "Your next order was rescheduled.",
          submit
        );
      } else if (action === "address") {
        await this.mutate(
          `/subscription/${id}/address`,
          {
            method: "PUT",
            body: {
              firstName: data.firstName,
              lastName: data.lastName,
              phone: data.phone,
              company: data.company,
              address1: data.address1,
              address2: data.address2,
              city: data.city,
              provinceCode: data.provinceCode,
              countryCode: String(data.countryCode).toUpperCase(),
              zip: data.zip,
            },
          },
          "Your shipping address was updated.",
          submit
        );
      } else if (action === "frequency") {
        const frequency = this.state.frequencies[Number(data.frequencyIndex)];
        const billingPolicy = frequency?.billingPolicy ?? frequency;
        const nextBillingDateEpoch = toEpochSeconds(
          firstValue(
            frequency?.nextBillingDateEpoch,
            this.state.activeSubscription?.nextBillingDateEpoch,
            this.state.activeSubscription?.nextBillingDate,
            dateValue(this.state.schedule[0])
          )
        );
        await this.mutate(
          `/subscription/${id}/frequency`,
          {
            method: "PUT",
            body: { billingPolicy, nextBillingDateEpoch },
          },
          "Your delivery frequency was updated.",
          submit
        );
      }
    }

    openModal(content) {
      const modal = this.root.querySelector(SELECTORS.modal);
      const container = this.root.querySelector(SELECTORS.modalContent);
      if (!modal || !container) return;
      container.innerHTML = content;
      if (typeof modal.showModal === "function") {
        modal.showModal();
      } else {
        modal.setAttribute("open", "");
      }
    }

    closeModal() {
      const modal = this.root.querySelector(SELECTORS.modal);
      if (!modal) return;
      if (typeof modal.close === "function" && modal.open) modal.close();
      else modal.removeAttribute("open");
    }
  }

  const initPortals = (scope = document) => {
    scope.querySelectorAll("[data-loop-portal]").forEach((portal) => {
      if (portal.dataset.loopInitialized === "true") return;
      portal.dataset.loopInitialized = "true";
      if (portal.querySelector("[data-loop-status]")) new LoopPortal(portal);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initPortals());
  } else {
    initPortals();
  }

  document.addEventListener("shopify:section:load", (event) => initPortals(event.target));
})();
